import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Gem, Users, TrendingDown } from "lucide-react";
import api, { errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { inr, num, pct, periodLabel } from "../lib/format";
import { Loading, PageHeader, StatCard, StatusBadge, StatusDot, SectionTitle, Empty } from "./kit";
import { StoreCurrent, PaceChart, Diagnostic } from "./StaffPerformanceView";
import SubmissionTracker from "./SubmissionTracker";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";

function Insights({ d, cats }) {
  const s = d.expectation.sales;
  const catRows = cats.map((c) => ({ ...c, e: d.cat_expectation[c.key] })).filter((c) => c.e.target);
  const worstCat = catRows.sort((a, b) => (a.e.pace ?? 999) - (b.e.pace ?? 999))[0];
  const attention = d.staff.filter((x) => x.status === "critical" || x.status === "behind");
  const cards = [
    { icon: TrendingDown, q: "Where is my store behind?", a: s.gap < 0 ? `${inr(-s.gap)} behind` : `${inr(s.gap)} ahead`, sub: `${pct(s.pace || 0, 0)} of expected pace`, tid: "insight-store-gap" },
    { icon: AlertTriangle, q: "Which KPI is causing the gap?", a: d.diagnostic.biggest?.label || "None", sub: d.diagnostic.biggest?.why || "All KPIs on pace", tid: "insight-kpi-gap" },
    { icon: Gem, q: "Which category is causing the gap?", a: worstCat && worstCat.e.gap < 0 ? worstCat.name : "None", sub: worstCat ? `${inr(worstCat.e.gap)} vs expected` : "", tid: "insight-category-gap" },
    { icon: Users, q: "Which staff needs attention?", a: attention.length ? attention.map((x) => x.name).join(", ") : "No one", sub: `${attention.length} behind or critical`, tid: "insight-staff-attention" },
  ];
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
      {cards.map((c) => (
        <div key={c.q} className="bs-card p-6 bs-facet" data-testid={c.tid}>
          <c.icon className="w-5 h-5 text-[#C5A059]" />
          <div className="text-sm text-slate-500 mt-3">{c.q}</div>
          <div className="font-display text-2xl font-bold text-[#011E38] mt-1 leading-tight">{c.a}</div>
          <div className="text-xs text-slate-500 mt-1.5">{c.sub}</div>
        </div>
      ))}
    </div>
  );
}

function StaffTable({ rows, storeId }) {
  const navigate = useNavigate();
  return (
    <div className="bs-card overflow-x-auto">
      <table className="w-full text-left" data-testid="staff-performance-table">
        <thead className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
          <tr>{["Staff", "Target", "Actual", "Expected", "Gap", "Achievement", "Required/Day", "Days Available", "Status"].map((h, i) => <th key={h} className={`px-5 py-4 font-semibold ${i && i < 8 ? "text-right" : ""}`}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} onClick={() => navigate(`/staff/${r.id}?store=${storeId}`)} className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer" data-testid={`staff-row-${r.employee_id}`}>
              <td className="px-5 py-5"><div className="font-semibold text-[#011E38] text-base">{r.name}</div><div className="text-xs text-slate-500">{r.employee_id}{r.current_store_id !== storeId ? " · transferred" : ""}{r.staff_status && r.staff_status !== "active" ? ` · ${r.staff_status === "left" ? "left BlueStone" : r.staff_status}` : ""}</div></td>
              <td className="px-5 num text-right text-lg">{inr(r.sales.target)}</td>
              <td className="px-5 num text-right text-lg font-bold text-[#011E38]">{inr(r.sales.actual)}</td>
              <td className="px-5 num text-right text-lg">{inr(r.sales.expected)}</td>
              <td className={`px-5 num text-right text-lg font-semibold ${r.sales.gap < 0 ? "text-orange-700" : "text-green-700"}`}>{inr(r.sales.gap)}</td>
              <td className="px-5 num text-right text-lg">{pct(r.sales.achievement)}</td>
              <td className="px-5 num text-right text-lg">{inr(r.sales.required_daily)}</td>
              <td className="px-5 num text-right" data-testid={`staff-available-${r.employee_id}`}><span className="text-lg font-semibold">{r.availability?.available}</span><span className="text-slate-400">/{r.availability?.store_days}</span></td>
              <td className="px-5"><StatusBadge status={r.status} testid={`staff-status-${r.employee_id}`} /></td>
            </tr>
          ))}
        </tbody>
      </table>
      {!rows.length && <Empty>No staff in this store yet.</Empty>}
    </div>
  );
}

function KpiMatrix({ rows, cats }) {
  const cols = [
    ["walkins", "Walk-ins", (k) => num(k.walkins)], ["bills", "Bills", (k) => num(k.bills)],
    ["conversion", "Conv", (k) => pct(k.conversion)], ["sales", "Sales", (k) => inr(k.sales)], ["gms", "GMS", (k) => inr(k.gms)],
    ["atv", "ATV", (k) => inr(k.atv)], ["asp", "ASP", (k) => inr(k.asp)], ["upt", "UPT", (k) => num(k.upt, 2)],
    ...cats.map((c) => [c.key, c.name, (k) => inr(k.cats[c.key])]),
  ];
  return (
    <div className="bs-card overflow-x-auto">
      <table className="w-full text-left" data-testid="kpi-matrix">
        <thead className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
          <tr><th className="px-5 py-4">Staff</th>{cols.map((c) => <th key={c[0]} className="px-3 py-4 text-center">{c[1]}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-b border-slate-50" data-testid={`kpi-matrix-row-${r.employee_id}`}>
              <td className="px-5 py-4 font-semibold text-[#011E38]">{r.name}</td>
              {cols.map(([k, , f]) => (
                <td key={k} className="px-3 py-4 text-center">
                  <div className="flex items-center justify-center gap-1.5"><StatusDot status={r.kpi_status[k]} testid={`matrix-${r.employee_id}-${k}`} /><span className="num text-sm">{f(r.kpis)}</span></div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex flex-wrap gap-4 px-5 py-3 text-xs text-slate-500 border-t border-slate-100">
        {["ahead", "on_track", "behind", "critical"].map((s) => <span key={s} className="flex items-center gap-1.5"><StatusDot status={s} /> <StatusBadge status={s} /></span>)}
      </div>
    </div>
  );
}

function GapRecovery({ d, cats }) {
  const rows = [["Sales", d.expectation.sales], ["GMS", d.expectation.gms], ...cats.map((c) => [c.name, d.cat_expectation[c.key]])];
  return (
    <div className="grid lg:grid-cols-12 gap-6">
      <div className="lg:col-span-7 bs-card overflow-x-auto">
        <table className="w-full text-left" data-testid="store-recovery-table">
          <thead className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
            <tr>{["Metric", "Target", "Actual", "Gap", "Required/Day", "Normal/Day", "Additional", "Status"].map((h, i) => <th key={h} className={`px-4 py-4 ${i && i < 7 ? "text-right" : ""}`}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map(([l, e]) => (
              <tr key={l} className="border-b border-slate-50">
                <td className="px-4 py-4 font-semibold text-[#011E38]">{l}</td>
                <td className="px-4 num text-right">{inr(e.target)}</td>
                <td className="px-4 num text-right font-bold">{inr(e.actual)}</td>
                <td className={`px-4 num text-right ${e.gap < 0 ? "text-orange-700" : "text-green-700"}`}>{inr(e.gap)}</td>
                <td className="px-4 num text-right font-semibold text-[#011E38]">{inr(e.required_daily)}</td>
                <td className="px-4 num text-right">{inr(e.normal_daily)}</td>
                <td className="px-4 num text-right">{inr(e.additional_daily)}</td>
                <td className="px-4"><StatusBadge status={e.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="lg:col-span-5"><Diagnostic diag={d.diagnostic} testid="store-diagnostic" /></div>
    </div>
  );
}

function StoreFlow({ k, cats }) {
  const big = (l, v, tid, cls = "text-[#011E38]") => (
    <div data-testid={tid}><div className="text-xs text-slate-500">{l}</div><div className={`num text-3xl font-bold ${cls}`}>{v}</div></div>
  );
  return (
    <div className="grid lg:grid-cols-2 gap-5">
      <section className="bs-card p-6" data-testid="store-sales-flow">
        <div className="bs-eyebrow mb-4">Store sales</div>
        <div className="grid grid-cols-3 gap-4">
          {big("Gross Sales", inr(k.gross_sales), "store-gross-sales")}
          {big("Returns", `-${inr(k.return_sales)}`, "store-returns", "text-red-700")}
          {big("Net Sales", inr(k.net_sales), "store-net-sales")}
          {big("W/o Gold Coin", inr(k.sales_ex_coin), "store-sales-ex-coin", "text-slate-700")}
          {big("Gold Coin", inr(k.gold_coin), "store-gold-coin", "text-[#8a6d2f]")}
          {big("GMS / Plan", inr(k.gms), "store-gms-plan", "text-slate-700")}
        </div>
      </section>
      <section className="bs-card p-6" data-testid="store-products">
        <div className="bs-eyebrow mb-4">Store products</div>
        <div className="grid grid-cols-3 gap-4">
          {big("Gross", num(k.gross_units), "store-gross-products")}
          {big("Returned", `-${num(k.return_units)}`, "store-returned-products", "text-red-700")}
          {big("Net", num(k.units), "store-net-products")}
        </div>
        <div className="grid grid-cols-4 gap-2 mt-5">
          {cats.map((c) => (
            <div key={c.key} className="rounded-xl bg-slate-50 p-3 text-center" data-testid={`store-qty-${c.key}`}>
              <div className="text-[0.65rem] text-slate-500">{c.name}</div><div className="num text-xl font-bold text-[#011E38]">{num(k.qty?.[c.key])}</div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-4 gap-2 mt-3 text-center text-xs text-slate-500">
          <div>ATV <b className="num text-[#011E38] block text-base">{inr(k.atv)}</b></div>
          <div>ASP <b className="num text-[#011E38] block text-base">{inr(k.asp)}</b></div>
          <div>UPT <b className="num text-[#011E38] block text-base">{num(k.upt, 2)}</b></div>
          <div>Conv <b className="num text-[#011E38] block text-base">{pct(k.conversion)}</b></div>
        </div>
      </section>
    </div>
  );
}

export default function StoreDashboard({ storeId }) {
  const { month, basis, range } = useAuth();
  const [d, setD] = useState(null);
  const [cats, setCats] = useState([]);
  const [err, setErr] = useState("");

  useEffect(() => {
    setD(null);
    Promise.all([api.get(`/performance/store/${storeId}`, { params: { month, basis, start: range?.start, end: range?.end } }), api.get("/categories")])
      .then(([p, c]) => { setD(p.data); setCats(c.data); })
      .catch((e) => setErr(errMsg(e)));
  }, [storeId, month, basis, range]);

  if (err) return <Empty>{err}</Empty>;
  if (!d) return <Loading />;
  const s = d.expectation.sales;
  const g = d.expectation.gms;

  return (
    <div className="space-y-10">
      <PageHeader eyebrow={`${periodLabel(d.period)} · ${d.days.elapsed}/${d.days.total} working days`} title={d.store_info.name} testid="store-dashboard-title"
        right={<StatusBadge status={d.status} testid="store-dashboard-status" large />} />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <StatCard label={basis === "ex_coin" ? "Net Sales w/o Coin" : "Store Net Sales"} value={inr(s.actual)} sub={`${pct(s.achievement)} of ${inr(s.target)}`} testid="store-actual" accent />
        <StatCard label="Expected" value={inr(s.expected)} sub="by today" testid="store-expected" />
        <StatCard label="Gap" value={`${inr(Math.abs(s.gap))}`} sub={s.gap < 0 ? "Behind" : "Ahead"} testid="store-gap" />
        <StatCard label="Required / Day" value={inr(s.required_daily)} sub={`Normal ${inr(s.normal_daily)}`} testid="store-required-daily" />
        <StatCard label="Period Target" value={inr(s.target)} testid="store-target" />
        <StatCard label="Achievement" value={pct(s.achievement)} testid="store-achievement" />
        <StatCard label="GMS" value={inr(g.actual)} sub={`of ${inr(g.target)} · ${pct(g.achievement)}`} status={g.status} testid="store-gms" />
        <StatCard label="Days Left" value={d.days.remaining} sub="working days" testid="store-days-left" />
      </div>

      <StoreFlow k={d.kpis} cats={cats} />

      <Insights d={d} cats={cats} />

      <Tabs defaultValue="staff">
        <TabsList className="h-12 bg-white border border-slate-200 rounded-full p-1">
          {[["staff", "Staff"], ["matrix", "KPI Matrix"], ["submissions", "Submissions & Closing"], ["gap", "Gap & Recovery"], ["store", "Store KPIs"]].map(([v, l]) => (
            <TabsTrigger key={v} value={v} className="rounded-full px-4 data-[state=active]:bg-[#011E38] data-[state=active]:text-white" data-testid={`tab-${v}`}>{l}</TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="staff" className="mt-6"><StaffTable rows={d.staff} storeId={storeId} /></TabsContent>
        <TabsContent value="matrix" className="mt-6"><KpiMatrix rows={d.staff} cats={cats} /></TabsContent>
        <TabsContent value="submissions" className="mt-6"><SubmissionTracker storeId={storeId} /></TabsContent>
        <TabsContent value="gap" className="mt-6"><GapRecovery d={d} cats={cats} /></TabsContent>
        <TabsContent value="store" className="mt-6 space-y-6">
          <StoreCurrent k={d.kpis} cats={cats} testid="store-kpis" />
          <div className="bs-card p-6"><SectionTitle eyebrow="Cumulative" title="Sales pace vs expected" /><PaceChart series={d.series} height={280} /></div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
